/**
 * Phase 2 Brand Kits — 品牌資產包型別
 * -----------------------------------------------------------------------------
 * 對齊後端 ``/hai-creative/api/brand_kits/*`` 7 endpoints：
 *   - 5 CRUD（create / list / get / update / remove）
 *   - 2 logo upload（intent / finalize），走「3-step：intent → GCS PUT → finalize」
 *
 * 設計重點：
 *   - **三 slot logo**：``primary`` / ``mono`` / ``square``；同一個 brand_kit doc 內
 *     的巢狀欄位，**非**獨立 collection（跟 ``UploadedImage`` 不同）
 *   - **logo_urls 唯讀（PATCH 不可帶）**：必須走 intent + finalize，後端有 magic byte
 *     驗 + slot 防 hijack
 *   - **brand_kit_id immutable**：想換重建一份（PATCH body 不可帶 brand_kit_id）
 *   - **每個 LogoSlotItem 持 fresh signed download URL**（TTL 60min；過期重打 GET）
 *   - **soft delete**：GCS 物件保留（迭代不刪）
 *
 * @see docs/BRAND_KITS.md
 */

import { mapCoverDto, type Cover, type CoverDto } from './cover';

// ─── DTO（snake_case，後端原樣 JSON）─────────────────────────────────────────

export interface BrandColorsDto {
  /**
   * ⚠️ **這四欄是「逗號分隔多值」的 CSV 字串**,不是單一色碼(BE 2026-08-17
   * `212ff1a`;owner 拍板「文字 + , 即可,類似 CSV」)。
   *
   * 例:`"#1E3A5F,#2E75B6"`。型別維持 `string` 是刻意的 —— 既有單值 doc 與生成端
   * 讀法零改(消費端把值串進 prompt 文字,CSV 天然相容),上限放寬到 200 字。
   * **讀取要 `split(',')` + trim,寫入 `join(',')`**;不要當成單一 hex 直接塞進 CSS。
   */
  primary: string;
  secondary: string | null;
  /** 強調色(BK-1;forward-compat 加欄,舊 doc → null)。 */
  accent: string | null;
  /** 文字色(D3 `colors.text`)。 */
  text: string | null;
  /** 背景色(D3 `colors.bg`)。 */
  bg: string | null;
  palette: string[];
}

/**
 * `BrandColorsDto` 的鍵名 runtime 清單 —— 契約鎖對 BE `BrandColors.model_fields` 用
 * (方向「BE ⊆ FE」;同 `LIBRARY_ITEM_DTO_KEYS` 慣例)。DTO 加欄忘了補這裡 → tsc 紅。
 */
export const BRAND_COLORS_DTO_KEYS = [
  'primary', 'secondary', 'accent', 'text', 'bg', 'palette',
] as const;

type _MissingBrandColorsKey = Exclude<
  keyof BrandColorsDto,
  (typeof BRAND_COLORS_DTO_KEYS)[number]
>;
const _brandColorsKeysExhaustive: _MissingBrandColorsKey extends never ? true : never = true;
void _brandColorsKeysExhaustive;

export interface BrandFontsDto {
  heading: string;
  body: string;
}

/**
 * 每個 slot 內持一個 ``LogoSlotItem`` 或 ``null``（沒上傳）。
 *
 * R50.3 — Beta v0.0.1 BE 砍 `gcs_path` field(commit 67f0826 P0 security:bucket
 * reconnaissance leak,brand_kits LogoSlotItem 跟 uploads 同款 fix)。
 */
export interface LogoSlotItemDto {
  download_url: string;
  download_url_expires_datetime: string;
}

export interface LogoSlotsResponseDto {
  primary: LogoSlotItemDto | null;
  mono: LogoSlotItemDto | null;
  square: LogoSlotItemDto | null;
  /** 反白版(深色底用);BE 2026-08-17 加,舊回應沒有這個鍵 → mapper 收成 null。 */
  dark?: LogoSlotItemDto | null;
}

export interface BrandKitDto {
  brand_kit_id: string;
  name: string;
  tone_of_voice: string | null;
  style_keywords: string[];
  colors: BrandColorsDto;
  fonts: BrandFontsDto | null;
  logo_urls: LogoSlotsResponseDto;
  prohibited: string[];
  /** ABCD 評分用:品牌名變體(縮寫/別名/翻譯)max 20。2026-05-18 BE MR !27 新增;舊 doc 回讀為 `[]`。 */
  brand_variations: string[];
  /** ABCD 評分用:品牌產品名 max 20。 */
  branded_products: string[];
  /** ABCD 評分用:產品類別 max 20。 */
  branded_products_categories: string[];
  /** ABCD 評分用:常用 CTA 文案 max 20。 */
  branded_call_to_actions: string[];
  /** 這份規範在講什麼(D3 `intro`;≤500)。BE 2026-08-17 加,舊 doc → null。 */
  intro: string | null;
  /** 目標受眾(D3 `audience`;≤200)。 */
  audience: string | null;
  /**
   * 允許略過警告直接產出(D3 `skipWarn`)。
   *
   * ⚠️ **後端目前純儲存、完全不判讀**(BE `d1ea055` 查證:compliance 模組做的是
   * 法規紅線,與「內容是否違反這份品牌規範」無關;`prohibited` 只是塞進 prompt
   * 叫 AI 別用,產出後沒有任何比對)。所以**不是 gate 還沒接線,是偵測根本不存在**
   * → UI 必須標「目前僅記錄,尚未套用到生成」,不可講成「會擋住生成」。
   */
  allow_skip_warning: boolean;
  created_datetime: string;
  updated_datetime: string;
  deleted_datetime: string | null;
  /**
   * T4(docs/50)— `GET /brand_kits` list item 多帶的引用次數(引用此 brand_kit 的
   * active project 數,reverse-lookup,`≥0`)。**僅 list 端點回此欄**;單筆
   * get/create/update/delete/duplicate 回純 entity 不含 → optional,mapper 缺值回退 0。
   */
  ref_count?: number;
  /**
   * R245(BE docs/92)— 封面三態(UI 卡片門面圖)。
   *
   * ⚠️ **跟 `logo_urls` 完全無關**:logo 是餵給生成端的品牌資產(三 slot / GCS /
   * signed URL 15 分過期 / intent+finalize 兩段式);cover 只是卡片門面圖,base64
   * 直接存在 doc 內、永不過期、一次 multipart 就寫完。兩者在同一個回應裡,不要
   * 因為都是「圖」就合併處理。
   *
   * optional 的理由同 `ProjectDto.cover`(BE 未部署的過渡期)。
   */
  cover?: CoverDto;
}

/** ``POST /brand_kits/{id}/logo/intent`` 的 ``result``。比一般 intent 多 ``slot``。 */
export interface CreateLogoIntentResultDto {
  upload_id: string;
  upload_url: string;
  expires_datetime: string;
  max_size_bytes: number;
  allowed_mime_types: string[];
  slot: LogoSlot;
}

// ─── 前端 camelCase ──────────────────────────────────────────────────────────

/** 三 slot logo 列表；型別常數（runtime 可 import） */
/**
 * Logo slot 值域(= BE `LogoSlot` Literal;path 的一部分,BE 用它防 path traversal)。
 *
 * `dark` = **反白版(深色底用)**,BE 2026-08-17 加。⚠️ 與 `mono` 不同:`mono` 是
 * 去彩色的單色版,`dark` 是在深色背景上可辨識的版本(通常反白或加亮),
 * 同一份品牌兩者可能都有且長得不一樣。
 */
export const LOGO_SLOTS = ['primary', 'mono', 'square', 'dark'] as const;
export type LogoSlot = (typeof LOGO_SLOTS)[number];

export interface BrandColors {
  /** CSV 多值(見 {@link BrandColorsDto.primary});讀寫一律過 `colorsCsv` 那組工具。 */
  primary: string;
  secondary: string | null;
  accent: string | null;
  text: string | null;
  bg: string | null;
  palette: string[];
}

export interface BrandFonts {
  heading: string;
  body: string;
}

export interface LogoSlotItem {
  /**
   * R50.3 — Beta v0.0.1 BE 砍 `gcsPath`(P0 security)。持久化只存 logo slot existence,
   * 顯示時重打 ``GET /brand_kits/{id}`` 拿 fresh `downloadUrl`。
   *
   * Signed read URL;TTL 15min,過期重打 ``GET /brand_kits/{id}`` 取 fresh。
   */
  downloadUrl: string;
  downloadUrlExpiresDatetime: string;
}

export interface LogoSlots {
  primary: LogoSlotItem | null;
  mono: LogoSlotItem | null;
  square: LogoSlotItem | null;
  dark: LogoSlotItem | null;
}

export interface BrandKit {
  brandKitId: string;
  name: string;
  toneOfVoice: string | null;
  styleKeywords: string[];
  colors: BrandColors;
  fonts: BrandFonts | null;
  logoUrls: LogoSlots;
  prohibited: string[];
  /** ABCD 評分用:品牌名變體 max 20(R43 新增,對應 BE MR !27)。 */
  brandVariations: string[];
  brandedProducts: string[];
  brandedProductsCategories: string[];
  brandedCallToActions: string[];
  createdDatetime: string;
  updatedDatetime: string;
  deletedDatetime: string | null;
  /**
   * T4(docs/50)— 引用此 brand kit 的 active project 數。僅 `list()` 回真值;單筆
   * get/create/update/remove/duplicate 回退 `0`(BE 不在純 entity 端點回此欄)。
   */
  refCount: number;
  /** R245 — 封面三態;與 `logoUrls` 無關,詳見 `types/cover.ts`。 */
  cover: Cover;
  /** 這份規範在講什麼(≤500);舊 doc → null。 */
  intro: string | null;
  /** 目標受眾(≤200)。 */
  audience: string | null;
  /** 允許略過警告 —— ⚠️ 後端純儲存不判讀,見 {@link BrandKitDto.allow_skip_warning}。 */
  allowSkipWarning: boolean;
}

/** :type:`CreateLogoIntentResultDto` 對應的前端型別。 */
export interface CreateLogoIntentResult {
  uploadId: string;
  uploadUrl: string;
  expiresDatetime: string;
  maxSizeBytes: number;
  allowedMimeTypes: string[];
  slot: LogoSlot;
}

// ─── DTO → camelCase mapping ─────────────────────────────────────────────────

function mapBrandColorsDto(dto: BrandColorsDto): BrandColors {
  return {
    primary: dto.primary,
    secondary: dto.secondary,
    // 三個 forward-compat 欄:舊 build 的回應沒有這些鍵 → null(不是 undefined,
    // 免得下游 `?? ''` 與 `=== null` 兩種寫法各自為政)
    accent: dto.accent ?? null,
    text: dto.text ?? null,
    bg: dto.bg ?? null,
    palette: dto.palette,
  };
}

function mapBrandFontsDto(dto: BrandFontsDto | null): BrandFonts | null {
  if (!dto) return null;
  return { heading: dto.heading, body: dto.body };
}

function mapLogoSlotItemDto(dto: LogoSlotItemDto | null | undefined): LogoSlotItem | null {
  if (!dto) return null;
  return {
    // R50.3 — gcs_path 已從 BE response 砍除(P0 security)
    downloadUrl: dto.download_url,
    downloadUrlExpiresDatetime: dto.download_url_expires_datetime,
  };
}

function mapLogoSlotsDto(dto: LogoSlotsResponseDto): LogoSlots {
  return {
    primary: mapLogoSlotItemDto(dto.primary),
    mono: mapLogoSlotItemDto(dto.mono),
    square: mapLogoSlotItemDto(dto.square),
    // 舊 doc 沒有 `dark`(BE 2026-08-17 才加)→ undefined 進 mapper 一樣回 null
    dark: mapLogoSlotItemDto(dto.dark),
  };
}

export function mapBrandKitDto(dto: BrandKitDto): BrandKit {
  return {
    brandKitId: dto.brand_kit_id,
    name: dto.name,
    toneOfVoice: dto.tone_of_voice,
    styleKeywords: dto.style_keywords,
    colors: mapBrandColorsDto(dto.colors),
    fonts: mapBrandFontsDto(dto.fonts),
    logoUrls: mapLogoSlotsDto(dto.logo_urls),
    prohibited: dto.prohibited,
    // R43 ABCD:`?? []` 防 BE 漂值(2026-05-18 之前 doc 沒這 4 欄,BE `default_factory=list`
    // 補 []。Mapper 邊界保守再 `??` 一次 normalize,守 R41.0 「mapper 邊界 normalize」契約)。
    brandVariations: dto.brand_variations ?? [],
    brandedProducts: dto.branded_products ?? [],
    brandedProductsCategories: dto.branded_products_categories ?? [],
    brandedCallToActions: dto.branded_call_to_actions ?? [],
    createdDatetime: dto.created_datetime,
    updatedDatetime: dto.updated_datetime,
    deletedDatetime: dto.deleted_datetime,
    refCount: dto.ref_count ?? 0,
    cover: mapCoverDto(dto.cover),
    // BE 2026-08-17 新三欄;舊 build / 舊 doc 沒有 → null / false(fail-soft,同 ABCD 那批的處置)
    intro: dto.intro ?? null,
    audience: dto.audience ?? null,
    allowSkipWarning: dto.allow_skip_warning ?? false,
  };
}

export function mapCreateLogoIntentResultDto(
  dto: CreateLogoIntentResultDto,
): CreateLogoIntentResult {
  return {
    uploadId: dto.upload_id,
    uploadUrl: dto.upload_url,
    expiresDatetime: dto.expires_datetime,
    maxSizeBytes: dto.max_size_bytes,
    allowedMimeTypes: dto.allowed_mime_types,
    slot: dto.slot,
  };
}

// ─── 色票 CSV(BE 2026-08-17)──────────────────────────────────────────────────
//
// ⚠️ `colors.{primary,secondary,accent,text,bg}` 全部是**逗號分隔的字串**,不是
// 陣列(owner 拍板「文字 + , 即可」)。所以**任何把這些值直接丟進 CSS 的地方都
// 是錯的** —— `background: #0D9488,#111111` 是無效宣告,整條被瀏覽器丟掉,而且
// 畫面上看起來只是「顏色沒出現」,不會有任何錯誤訊息。判準放在型別層(而不是某
// 個元件裡)就是為了讓四個呼叫端共用同一份,不再各自 split 一次。

const BRAND_HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * CSV 色票字串 → 陣列。
 *
 * 空 / null → 空陣列;逐段 trim 並丟掉空段(`"#111, ,#222"` → 兩個)。
 * ⚠️ **不驗色碼格式** —— 舊資料可能是「藍色」這種文字(BE 型別只是 str),硬濾掉
 * 會讓使用者的既有值在編輯時人間蒸發。要進 CSS 的地方自己再過 {@link isBrandHex}。
 */
export function parseCsvColors(csv: string | null | undefined): string[] {
  if (!csv) return [];
  return csv
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);
}

/** 陣列 → CSV(空陣列回空字串,呼叫端自己決定要不要轉 null)。 */
export function toCsvColors(list: string[]): string {
  return list
    .map((c) => c.trim())
    .filter(Boolean)
    .join(',');
}

/** `#RGB` / `#RRGGBB`;其餘(存量的中文色名、空字串)不猜、不進 CSS。 */
export function isBrandHex(color: string): boolean {
  return BRAND_HEX.test(color);
}

/**
 * 品牌主色的第一個合法 HEX —— 給「拿品牌色當底」的呼叫端用(專案封面 fallback /
 * 品牌包卡色帶 / 專案列的小色點)。查無回 null,呼叫端走自己的中性 fallback。
 */
export function brandPrimaryHex(colors: BrandColors | null | undefined): string | null {
  return parseCsvColors(colors?.primary).find(isBrandHex) ?? null;
}
